<script setup>
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useDateFnsLocale } from 'shared/composables/useDateFnsLocale';
import {
  useDebthubLink,
  DEBTHUB_STATUS,
} from 'dashboard/composables/useDebthubLink';

// Correlação Chatwoot -> DEBTHUB com CONFIRMAÇÃO HUMANA, dentro do diálogo da
// conversa (nunca no card do board: a resolução é síncrona contra outro
// sistema, e 50 cards visíveis virariam 50 chamadas HTTP externas).
//
// O DESENHO SEGUE AS FREQUÊNCIAS MEDIDAS em produção, não o caso bonito:
// 72,10% dos telefones não casam com pessoa nenhuma — `none` é o estado
// NORMAL desta tela, e por isso é uma linha discreta, não um erro.
//
// O CPF É O DESTAQUE, e o nome não. Medido: em candidato único, 67,35% dos
// pares não têm um único token em comum com o nome do Chatwoot (que é o push
// name do WhatsApp), e no caso ambíguo o nome aponta um vencedor em 3,00% das
// vezes. Dar peso visual ao nome levaria o humano a decidir errado.
const props = defineProps({
  conversationDisplayId: { type: Number, required: true },
  // `pessoa_id` do candidato/vínculo cujo painel de detalhe está aberto no
  // diálogo-pai, ou null com o painel fechado. Os dois formatos de payload
  // (candidato e vínculo) carregam `pessoa_id` (ver
  // Debt::DebthubLinkResolver#candidate_payload / #link_payload), então uma
  // única chave basta — nunca há candidato E vínculo ao mesmo tempo.
  detailOpenKey: { type: [String, Number], default: null },
});

// A expansão lateral vive no diálogo-pai (KanbanConversationModal), não
// aqui: é lá que ela precisa ficar para ampliar a MESMA armadilha de foco
// (useFocusTrap busca dentro do contêiner do diálogo inteiro) e para
// disputar largura com o restante do conteúdo. Este painel só entrega o
// candidato ou o vínculo que foi clicado — o subject já é o que
// useDebthubLink carregou na abertura da conversa, então abrir os detalhes
// não soma nenhuma chamada de rede.
const emit = defineEmits(['viewDetails', 'invalidateDetails']);

const { t, te } = useI18n();
const { dateFnsLocale } = useDateFnsLocale();

const {
  isLoading,
  hasError,
  isSubmitting,
  actionErrorKey,
  isEnabled,
  status,
  link,
  candidates,
  hasSingleCandidate,
  load,
  confirm,
  reject,
  undo,
} = useDebthubLink(() => props.conversationDisplayId);

onMounted(load);

// `confirm`/`reject`/`undo` são assíncronos e substituem `payload` inteiro
// na resposta (useDebthubLink#write) — o candidato ou vínculo que o painel
// de detalhe está mostrando pode ter sido descartado por essa troca
// enquanto o painel seguia aberto, e nada FORA de KanbanConversationModal
// (dono do `detailRequest`) tem como saber disso sozinho: o objeto que ele
// guarda é um retrato, não uma referência viva. `flush: 'post'` importa: o
// diálogo-pai devolve o foco ao gatilho ao fechar, e antes do DOM ser
// atualizado o nó do candidato descartado ainda existe (`isConnected` daria
// falso positivo, focando um nó que o próprio Vue remove um instante depois).
watch(
  [candidates, link],
  () => {
    if (props.detailOpenKey === null) return;
    const stillOpen = link.value
      ? link.value.pessoa_id === props.detailOpenKey
      : candidates.value.some(
          candidate => candidate.pessoa_id === props.detailOpenKey
        );
    if (!stillOpen) emit('invalidateDetails');
  },
  { flush: 'post' }
);

// Enquanto carrega, `isEnabled` é falso e o bloco não aparece — o que é
// diferente de conta sem DEBTHUB, onde ele NUNCA aparece. Os dois casos são
// separados aqui para o painel não piscar um título vazio.
const isVisible = computed(
  () => isLoading.value || hasError.value || isEnabled.value
);

const cpfOf = candidate => candidate.cpf || t('KANBAN.DEBTHUB.NO_CPF');

// `motivo` é uma lista de tokens do backend (telefone_exato,
// telefone_variante, acordo). Token novo, ainda sem tradução, aparece cru em
// vez de virar a própria chave de i18n na tela.
const reasonLabel = motivo =>
  (motivo ?? [])
    .map(token =>
      te(`KANBAN.DEBTHUB.REASON.${token}`)
        ? t(`KANBAN.DEBTHUB.REASON.${token}`)
        : token
    )
    .join(', ');

const formatDate = value =>
  value ? format(new Date(value), 'Pp', { locale: dateFnsLocale.value }) : '';

const confirmedByLabel = computed(() => {
  if (!link.value) return '';
  const date = formatDate(link.value.confirmed_at);
  const name = link.value.confirmed_by?.name;
  return name
    ? t('KANBAN.DEBTHUB.LINKED_BY', { name, date })
    : t('KANBAN.DEBTHUB.LINKED_AT', { date });
});

const STATUS_MESSAGES = {
  [DEBTHUB_STATUS.NONE]: 'KANBAN.DEBTHUB.NONE',
  [DEBTHUB_STATUS.REJECTED]: 'KANBAN.DEBTHUB.REJECTED',
  [DEBTHUB_STATUS.INSTITUTIONAL]: 'KANBAN.DEBTHUB.INSTITUTIONAL',
  [DEBTHUB_STATUS.UNAVAILABLE]: 'KANBAN.DEBTHUB.UNAVAILABLE',
};

const statusMessage = computed(() => {
  const key = STATUS_MESSAGES[status.value];
  return key ? t(key) : '';
});

// `unavailable` chega como HTTP 200: é degradação da consulta, não falha da
// requisição — por isso ela tem uma ação de tentar de novo, e os outros três
// estados de mensagem não (não há o que repetir).
const canRetryLookup = computed(
  () => status.value === DEBTHUB_STATUS.UNAVAILABLE
);

// `event.currentTarget` viaja no payload para o diálogo-pai devolver o foco
// exatamente ao botão que abriu o painel, ao fechá-lo — e não a "algum lugar
// dentro do diálogo".
const openLinkedDetails = event => {
  emit('viewDetails', {
    subject: link.value,
    kind: 'confirmed',
    trigger: event.currentTarget,
  });
};

const openCandidateDetails = (candidate, event) => {
  emit('viewDetails', {
    subject: candidate,
    kind: 'proposed',
    trigger: event.currentTarget,
  });
};
</script>

<template>
  <section
    v-if="isVisible"
    data-testid="kanban-debthub-panel"
    class="flex flex-col gap-2 px-4 py-3 border-b border-n-weak"
  >
    <h3 class="m-0 text-xs font-semibold uppercase text-n-slate-11">
      {{ t('KANBAN.DEBTHUB.TITLE') }}
    </h3>

    <p v-if="isLoading" role="status" class="m-0 text-sm text-n-slate-11">
      {{ t('KANBAN.DEBTHUB.LOADING') }}
    </p>

    <div v-else-if="hasError" role="alert" class="flex items-center gap-2">
      <p class="m-0 text-sm text-n-slate-11">
        {{ t('KANBAN.DEBTHUB.ERROR') }}
      </p>
      <NextButton
        slate
        faded
        xs
        :label="t('KANBAN.DEBTHUB.RETRY')"
        @click="load"
      />
    </div>

    <template v-else>
      <!-- Vínculo confirmado: a única ação é desfazer. Trocar de pessoa passa
           por desfazer primeiro, e é por isso que a lista de candidatos some
           daqui (o backend já a devolve vazia neste estado). -->
      <div
        v-if="status === DEBTHUB_STATUS.CONFIRMED && link"
        data-testid="kanban-debthub-confirmed"
        class="flex flex-col gap-1"
      >
        <!-- Ícone junto do texto: "vinculado" não pode depender só da cor
             (WCAG 1.4.1), e o rótulo textual sozinho já não dependia — o
             ícone é redundância deliberada, não decoração. -->
        <p
          class="m-0 flex items-center gap-1 text-xs font-medium text-n-teal-11"
        >
          <span aria-hidden="true" class="i-lucide-link-2 size-3 shrink-0" />
          {{ t('KANBAN.DEBTHUB.LINKED') }}
        </p>
        <p class="m-0 text-sm font-semibold tabular-nums text-n-slate-12">
          <template v-if="link.cpf">
            {{ t('KANBAN.DEBTHUB.CPF_LABEL') }} {{ link.cpf }}
          </template>
          <template v-else>{{ t('KANBAN.DEBTHUB.NO_CPF') }}</template>
        </p>
        <p v-if="link.nome" class="m-0 text-xs text-n-slate-11">
          {{ link.nome }}
        </p>
        <p class="m-0 text-xs text-n-slate-11">{{ confirmedByLabel }}</p>
        <!-- AÇÕES. "Detalhes" é a ação principal e vem primeiro: é ela que
             abre pasta, carteira, acordos, parcelas e o negócio do Pipedrive.
             Os links diretos ("abrir a pasta", "abrir contato") saíram daqui —
             viviam soltos como texto sublinhado no meio da leitura, competindo
             com a ação principal e sem hierarquia nenhuma. Eles continuam
             existindo, dentro do painel de detalhe, ao lado do dado que
             descrevem. -->
        <div class="flex flex-wrap items-center gap-2 mt-1">
          <!-- `blue faded` + `!text-n-blue-12`, e NÃO `blue solid`: o comentário
               do botão Confirmar, logo abaixo, já registra que branco sobre
               #2781F6 dá 3,78:1 e reprova 1.4.3. Usar `solid` aqui teria feito
               da ação principal a de pior contraste da tela. Medido nesta
               variante: 10,43:1 no claro, 10,84:1 no escuro. -->
          <NextButton
            blue
            faded
            sm
            class="!text-n-blue-12"
            icon="i-lucide-panel-right-open"
            data-testid="kanban-debthub-view-details"
            :label="t('KANBAN.DEBTHUB.VIEW_DETAILS')"
            :aria-label="t('KANBAN.DEBTHUB.VIEW_DETAILS_LINKED')"
            :aria-expanded="detailOpenKey === link.pessoa_id ? 'true' : 'false'"
            :aria-controls="
              detailOpenKey === link.pessoa_id
                ? 'kanban-debthub-detail-panel'
                : null
            "
            @click="openLinkedDetails"
          />
          <NextButton
            slate
            ghost
            sm
            :disabled="isSubmitting"
            :label="t('KANBAN.DEBTHUB.UNDO')"
            @click="undo"
          />
        </div>
      </div>

      <!-- Proposta: um candidato (25,89%) ou vários (2,01%). A lista é a mesma
           peça nos dois casos; o que muda é o título, porque a pergunta é
           outra ("é este?" contra "qual destes?"). -->
      <div
        v-else-if="status === DEBTHUB_STATUS.PROPOSED"
        data-testid="kanban-debthub-proposed"
        class="flex flex-col gap-2"
      >
        <p class="m-0 text-sm font-medium text-n-slate-12">
          {{
            hasSingleCandidate
              ? t('KANBAN.DEBTHUB.PROPOSE_ONE')
              : t('KANBAN.DEBTHUB.PROPOSE_MANY')
          }}
        </p>
        <p class="m-0 text-xs text-n-slate-11">
          {{ t('KANBAN.DEBTHUB.NAME_WARNING') }}
        </p>
        <ul class="flex flex-col gap-2 m-0 p-0 list-none">
          <li
            v-for="candidate in candidates"
            :key="candidate.pessoa_id"
            data-testid="kanban-debthub-candidate"
            class="flex flex-wrap items-center justify-between gap-2 p-2 rounded-lg bg-n-alpha-1"
          >
            <div class="min-w-0">
              <p class="m-0 text-sm font-semibold tabular-nums text-n-slate-12">
                {{ t('KANBAN.DEBTHUB.CPF_LABEL') }} {{ cpfOf(candidate) }}
              </p>
              <p
                v-if="candidate.nome"
                class="m-0 text-xs truncate text-n-slate-11"
              >
                {{ candidate.nome }}
              </p>
              <p class="m-0 text-xs text-n-slate-11">
                {{ reasonLabel(candidate.motivo) }}
              </p>
            </div>
            <div class="flex items-center gap-1 shrink-0">
              <!--
                !text-n-blue-12 pelo mesmo motivo já medido em
                KanbanPeriodFilter.vue: a variante compartilhada `blue faded`
                (bg-n-brand/10 text-n-blue-11) dá 4,05:1 no tema claro, abaixo
                de 4,5:1 (1.4.3). Aqui o custo é o maior da tela — este é o
                botão que amarra um contato de WhatsApp ao CPF de uma pessoa
                real, e o par `Descartar` (slate ghost) já passa a 15,19:1: a
                ação afirmativa era a menos legível das duas. O override vale
                nos dois temas (10,43:1 claro, 12,21:1 escuro, recalculados) e
                não toca o token compartilhado, usado em 30 arquivos fora
                daqui. `blue solid` NÃO é alternativa: branco sobre #2781F6 dá
                3,78:1.
              -->
              <NextButton
                blue
                faded
                xs
                class="!text-n-blue-12"
                :disabled="isSubmitting"
                :label="t('KANBAN.DEBTHUB.CONFIRM')"
                :aria-label="
                  t('KANBAN.DEBTHUB.CONFIRM_CANDIDATE', {
                    cpf: cpfOf(candidate),
                  })
                "
                @click="confirm(candidate.pessoa_id)"
              />
              <NextButton
                slate
                ghost
                xs
                :disabled="isSubmitting"
                :label="t('KANBAN.DEBTHUB.REJECT')"
                :aria-label="
                  t('KANBAN.DEBTHUB.REJECT_CANDIDATE', {
                    cpf: cpfOf(candidate),
                  })
                "
                @click="reject(candidate.pessoa_id)"
              />
              <NextButton
                slate
                ghost
                xs
                icon="i-lucide-panel-right-open"
                data-testid="kanban-debthub-view-details"
                :label="t('KANBAN.DEBTHUB.VIEW_DETAILS')"
                :aria-label="
                  t('KANBAN.DEBTHUB.VIEW_DETAILS_CANDIDATE', {
                    cpf: cpfOf(candidate),
                  })
                "
                :aria-expanded="
                  detailOpenKey === candidate.pessoa_id ? 'true' : 'false'
                "
                aria-controls="kanban-debthub-detail-panel"
                @click="openCandidateDetails(candidate, $event)"
              />
            </div>
          </li>
        </ul>
      </div>

      <!-- none / rejected / institutional_number / unavailable: nenhum deles
           tem lista para oferecer, e cada um diz uma coisa diferente. -->
      <div v-else class="flex items-center gap-2">
        <p
          data-testid="kanban-debthub-status-message"
          class="m-0 text-sm text-n-slate-11"
        >
          {{ statusMessage }}
        </p>
        <NextButton
          v-if="canRetryLookup"
          slate
          faded
          xs
          :disabled="isSubmitting"
          :label="t('KANBAN.DEBTHUB.RETRY')"
          @click="load"
        />
      </div>

      <p
        v-if="actionErrorKey"
        role="alert"
        class="m-0 flex items-start gap-1 text-xs text-n-ruby-11"
      >
        <span
          aria-hidden="true"
          class="i-lucide-triangle-alert size-3 shrink-0 mt-0.5"
        />
        {{ t(`KANBAN.DEBTHUB.ACTION_ERROR.${actionErrorKey}`) }}
      </p>
    </template>
  </section>
</template>
